import { inject, Injectable } from '@angular/core';
import { PureAbility } from '@casl/ability';
import { CookieService } from 'ngx-cookie-service';
import { NGXLogger } from 'ngx-logger';
import {
	BehaviorSubject,
	catchError,
	EMPTY,
	Observable,
	tap,
	throwError,
} from 'rxjs';
import {
	ApiResponse,
	BaseApiResponse,
	createAbility,
	UserRecordDto,
} from '@splitsecondconnect/ultramarine-shared';

import { UltramarineApiService } from '../api/ultramarine-api.service';
import { AppConfigService } from '../config/app/app.config.service';
import { DOCUMENT } from '@angular/common';

@Injectable()
export class AuthService {
	#config = inject(AppConfigService);
	document = inject(DOCUMENT);

	private _isAuthenticated$: BehaviorSubject<boolean> = new BehaviorSubject(
		null,
	);
	private _user$: BehaviorSubject<UserRecordDto> = new BehaviorSubject(null);

	constructor(
		private _pureAbility: PureAbility,
		private _logger: NGXLogger,
		private _apiService: UltramarineApiService,
		private _cookieService: CookieService,
	) {}

	get isAuthenticated$(): Observable<boolean> {
		return this._isAuthenticated$.asObservable();
	}

	get isAuthenticated(): boolean {
		return this._isAuthenticated$.value;
	}

	get user$(): Observable<UserRecordDto> {
		return this._user$.asObservable();
	}

	get userId(): number {
		return this._user$.value.id;
	}

	get username(): string {
		return this._user$.value.username;
	}

	get tenantName(): string {
		return this._user$.value.tenantName;
	}

	signIn(): Observable<ApiResponse<UserRecordDto>> {
		return this._apiService
			.post<ApiResponse<UserRecordDto>>(`auth/sign-in`, {})
			.pipe(
				tap((response: ApiResponse<UserRecordDto>) => {
					const user = response.data;
					if (user) {
						this._isAuthenticated$.next(true);
						this._user$.next(user);
						this.updateAbility(user.userRolePermissions);
					} else {
						this._isAuthenticated$.next(false);
						this._user$.next(null);
					}
				}),
				catchError((error: any) => {
					this._isAuthenticated$.next(false);
					this._user$.next(null);
					if (error.statusCode == 401) {
						this.navigateToPortal();
					} else {
						alert(JSON.stringify(error, null, 2));
					}
					return EMPTY;
				}),
			);
	}

	signOut(): Observable<BaseApiResponse> {
		return this._apiService.post(`auth/sign-out`, {}).pipe(
			tap((response: BaseApiResponse) => {
				this._isAuthenticated$.next(false);
				this._user$.next(null);
			}),

			catchError((error: any) => {
				return throwError(
					() =>
						new Error('An error occurred while fetching data from the server'),
				);
			}),
		);
	}

	updateAbility(userPermissions) {
		const ability = createAbility(userPermissions);
		this._pureAbility.update(ability.rules);
	}

	navigateToPortal() {
		const portalUrl = this.#config.config.portalUrl;
		const currentDomain = this.document.location.host;
		this.document.location.href = `${portalUrl}?referer=${currentDomain}`;
	}

	navigateToLogout() {
		const portalUrl = this.#config.config.portalUrl;
		const currentDomain = this.document.location.host;
		this.document.location.href = `${portalUrl}/logout?referer=${currentDomain}`;
	}
}
